import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import type { Game, Goal } from '../types';
import GoalDiagramPDF from './GoalDiagramPDF';
import GoalAnalyticsPDF from './GoalAnalyticsPDF';
import ShotPositionDiagramPDF from './ShotPositionDiagramPDF';
import ShotPositionAnalyticsPDF from './ShotPositionAnalyticsPDF';

interface GamePDFProps {
  game: Game;
  goals: Goal[];
}

const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: '#ffffff',
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    marginBottom: 10,
    fontWeight: 'bold',
    color: '#1b3968',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 10,
    fontWeight: 'bold',
    color: '#1b3968',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 10,
    gap: 20,
  },
  column: {
    flex: 1,
  },
  label: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 4,
  },
  value: {
    fontSize: 14,
  },
  statsContainer: {
    flexDirection: 'row',
    marginBottom: 20,
    gap: 20,
  },
  statBox: {
    flex: 1,
    padding: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 4,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  goalItem: {
    marginBottom: 15,
    padding: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 4,
  },
  goalTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#1b3968',
  },
  goalDetails: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 5,
  },
  visualizationContainer: {
    flex: 1,
    padding: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 4,
    backgroundColor: '#ffffff',
  },
  visualizationTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#1b3968',
    textAlign: 'center',
  },
  note: {
    fontSize: 10,
    color: '#6b7280',
    marginTop: 8,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 10,
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#6b7280',
  },
});

const GamePDF: React.FC<GamePDFProps> = ({ game, goals }) => {
  const savePercentage = game.shotsFaced !== null && game.saves !== null
    ? ((game.saves / game.shotsFaced) * 100).toFixed(1)
    : '—';

  const goalsAgainst = goals.length;

  const goalsPerPage = 5;
  const goalChunks = goals.reduce((acc, goal, i) => {
    const chunkIndex = Math.floor(i / goalsPerPage);
    if (!acc[chunkIndex]) {
      acc[chunkIndex] = [];
    }
    acc[chunkIndex].push(goal);
    return acc;
  }, [] as Goal[][]);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Game Analysis</Text>
          <View style={styles.row}>
            <View style={styles.column}>
              <Text style={styles.label}>Date</Text>
              <Text style={styles.value}>
                {game.date.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Opponent</Text>
              <Text style={styles.value}>{game.opponent}</Text>
            </View>
            <View style={styles.column}>
              <Text style={styles.label}>Competition Type</Text>
              <Text style={styles.value}>{game.competitionType}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.row}>
            <View style={styles.visualizationContainer}>
              <Text style={styles.visualizationTitle}>Goal Locations</Text>
              <GoalDiagramPDF goals={goals} width={250} height={187.5} />
              <Text style={styles.note}>
                Each point represents a goal's location
              </Text>
            </View>
            <View style={styles.visualizationContainer}>
              <Text style={styles.visualizationTitle}>Goal Distribution</Text>
              <GoalAnalyticsPDF goals={goals} width={250} height={187.5} />
              <Text style={styles.note}>
                Percentage of goals scored in each zone
              </Text>
            </View>
          </View>
          <View style={[styles.row, { marginTop: 20 }]}>
            <View style={styles.visualizationContainer}>
              <Text style={styles.visualizationTitle}>Shot Locations</Text>
              <ShotPositionDiagramPDF goals={goals} width={250} height={187.5} />
              <Text style={styles.note}>
                Each point represents where a shot was taken from
              </Text>
            </View>
            <View style={styles.visualizationContainer}>
              <Text style={styles.visualizationTitle}>Shot Distribution</Text>
              <ShotPositionAnalyticsPDF goals={goals} width={250} height={187.5} />
              <Text style={styles.note}>
                Percentage of shots taken from each zone
              </Text>
            </View>
          </View>
        </View>

        <Text 
          style={styles.pageNumber} 
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} 
          fixed 
        />
      </Page>

      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Game Statistics</Text>
          <View style={styles.statsContainer}>
            <View style={styles.statBox}>
              <Text style={styles.statLabel}>Shots Faced</Text>
              <Text style={styles.statValue}>{game.shotsFaced}</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statLabel}>Saves</Text>
              <Text style={styles.statValue}>{game.saves}</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statLabel}>Save Percentage</Text>
              <Text style={styles.statValue}>{savePercentage}%</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statLabel}>Goals Against</Text>
              <Text style={styles.statValue}>{goalsAgainst}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Goals Timeline</Text>
          {goals.map((goal, index) => (
            <View key={goal.id} style={styles.goalItem}>
              <Text style={styles.goalTitle}>
                Goal {index + 1}: {goal.title}
              </Text>
              <Text style={styles.value}>{goal.situationDescription}</Text>
            </View>
          ))}
        </View>

        <Text 
          style={styles.pageNumber} 
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} 
          fixed 
        />
      </Page>
    </Document>
  );
};

export default GamePDF;